import React from "react";
import { CheckCircle2, Lock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { type ChatTabStage } from "./RestorationRoomChat";
import { type Incident } from "./types";

// Single source of truth for what each stage is called and what order
// they happen in. The Behavior Incident Toolkit is explicitly sequential:
// Situation → Understand → Conversation → Resolution → (Extended Support
// is conditional and appears only when triggered).
export const STAGE_META: { key: ChatTabStage; num: number; label: string }[] = [
  { key: "incident", num: 1, label: "The Situation" },
  { key: "reflection", num: 2, label: "Understand" },
  { key: "conversation", num: 3, label: "The Conversation" },
  { key: "resolution", num: 4, label: "Resolution" },
];

export const EXTENDED_STAGE: { key: ChatTabStage; num: number; label: string } = {
  key: "extended",
  num: 5,
  label: "Extended Support",
};

export type StageCompletion = Record<ChatTabStage, boolean>;

// Whether the Extended Support stage is part of this incident's flow.
// Mirrors the same condition the page header / chat use to decide
// whether to render the 5th step.
export const showsExtendedStage = (incident: Incident): boolean =>
  incident.removal_type === "formal_removal" ||
  !!incident.iss_work_plan?.trim() ||
  !!incident.return_to_class_plan?.trim() ||
  !!incident.reentry_conversation_needed;

// What "completed" means for each stage. These checks mirror the
// progression logic already encoded in `resumeStepFor` so the tracker
// agrees with how the chat decides where to resume.
export const getStageCompletion = (incident: Incident): StageCompletion => {
  const desc = incident.incident_description?.trim();
  const name = incident.student_identifier?.trim();
  const incidentDone = !!desc && !!name;

  // Understand: we've captured why the behavior happened (motivation
  // gets stored as a "Motivation:" line in conversation_notes).
  const reflectionDone =
    incidentDone && /^Motivation:/im.test(incident.conversation_notes || "");

  // Conversation: either the leader explicitly marked it complete OR an
  // outcome was logged.
  const conversationDone =
    reflectionDone &&
    (incident.conversation_completed ||
      /^Outcome:/im.test(incident.conversation_notes || ""));

  // Resolution: a follow-up plan exists OR the incident is closed.
  const resolutionDone =
    conversationDone &&
    (!!incident.behavior_follow_up?.trim() || incident.status === "closed");

  // Extended Support is closed when the documented plans are in place.
  const extendedDone =
    !!incident.iss_work_plan?.trim() &&
    !!incident.return_to_class_plan?.trim();

  return {
    incident: incidentDone,
    reflection: reflectionDone,
    conversation: conversationDone,
    resolution: resolutionDone,
    extended: extendedDone,
  };
};

// A stage is reachable if it's the active stage, OR every prior stage
// in the visible list is complete. Reaching forward is gated; reaching
// back to anything already complete is always allowed.
export const isStageReachable = (
  target: ChatTabStage,
  activeStage: ChatTabStage,
  completion: StageCompletion,
  stages: { key: ChatTabStage }[],
): boolean => {
  if (target === activeStage) return true;
  const targetIdx = stages.findIndex((s) => s.key === target);
  const activeIdx = stages.findIndex((s) => s.key === activeStage);
  if (targetIdx < 0) return false;
  // Going backward to a prior stage: always allowed.
  if (targetIdx <= activeIdx) return true;
  // Going forward: every stage strictly before the target must be complete.
  for (let i = 0; i < targetIdx; i += 1) {
    if (!completion[stages[i].key]) return false;
  }
  return true;
};

// Short tooltip text for a locked stage explaining what the user has
// to finish first. Returns the immediately-prior incomplete stage's
// label so the message is actionable.
export const lockReasonFor = (
  target: ChatTabStage,
  completion: StageCompletion,
  stages: { key: ChatTabStage; label: string }[],
): string => {
  const targetIdx = stages.findIndex((s) => s.key === target);
  for (let i = 0; i < targetIdx; i += 1) {
    if (!completion[stages[i].key]) {
      return `Finish ${stages[i].label} first.`;
    }
  }
  return "Complete the prior steps first.";
};

interface StageTrackerProps {
  variant: "pills" | "compact";
  incident: Incident;
  activeStage: ChatTabStage;
  onJump: (stage: ChatTabStage) => void;
  isReadOnly?: boolean;
}

// Unified stage tracker used both in the page header (variant="pills")
// and inside the chat (variant="compact"). Same completion + locking
// rules in both places so the UI is predictable.
export const StageTracker: React.FC<StageTrackerProps> = ({
  variant,
  incident,
  activeStage,
  onJump,
  isReadOnly = false,
}) => {
  const stages = [
    ...STAGE_META,
    ...(showsExtendedStage(incident) ? [EXTENDED_STAGE] : []),
  ];
  const completion = getStageCompletion(incident);
  const activeIdx = stages.findIndex((s) => s.key === activeStage);

  if (variant === "pills") {
    return (
      <TooltipProvider delayDuration={150}>
        <div
          className="flex gap-0 mt-5 border-b border-white/20"
          role="tablist"
          aria-label="Behavior Incident Toolkit progress"
        >
          {stages.map((stage, idx) => {
            const isActive = idx === activeIdx;
            const isCompleted = completion[stage.key] && !isActive;
            const reachable = isStageReachable(
              stage.key,
              activeStage,
              completion,
              stages,
            );
            const isLocked = !reachable && !isActive;
            const disabled = isReadOnly || isLocked;

            const button = (
              <button
                type="button"
                role="tab"
                aria-current={isActive ? "step" : undefined}
                aria-disabled={disabled}
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  onJump(stage.key);
                }}
                className={cn(
                  "px-4 py-2.5 text-xs md:text-sm font-medium border-b-2 flex items-center gap-1.5 transition-colors",
                  isActive
                    ? "border-white text-white"
                    : isCompleted
                      ? "border-transparent text-white/85 hover:text-white hover:border-white/40"
                      : "border-transparent text-white/40",
                  disabled
                    ? "cursor-not-allowed"
                    : "cursor-pointer hover:border-white/30",
                )}
              >
                <span
                  className={cn(
                    "w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0",
                    isActive
                      ? "bg-white text-bodhi-teal"
                      : isCompleted
                        ? "bg-white/40 text-white"
                        : "bg-white/10 text-white/50 border border-white/20",
                  )}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="w-3 h-3" aria-hidden />
                  ) : isLocked ? (
                    <Lock className="w-3 h-3" aria-hidden />
                  ) : (
                    stage.num
                  )}
                </span>
                <span className="hidden sm:inline">{stage.label}</span>
              </button>
            );

            if (!isLocked) return <div key={stage.key} className="contents">{button}</div>;
            return (
              <Tooltip key={stage.key}>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>{button}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {lockReasonFor(stage.key, completion, stages)}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    );
  }

  // Compact variant — used inside the chat container. Smaller pills,
  // arrows between, locked steps muted with a tiny lock icon. Same
  // click rules as the pills variant.
  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="px-4 py-3 bg-white border-b border-gray-200 flex items-center gap-1 text-[11px] overflow-x-auto"
        role="tablist"
        aria-label="Behavior Incident Toolkit progress"
      >
        {stages.map((stage, idx) => {
          const isActive = idx === activeIdx;
          const isCompleted = completion[stage.key] && !isActive;
          const reachable = isStageReachable(
            stage.key,
            activeStage,
            completion,
            stages,
          );
          const isLocked = !reachable && !isActive;
          const disabled = isReadOnly || isLocked;

          const button = (
            <button
              type="button"
              role="tab"
              aria-current={isActive ? "step" : undefined}
              aria-disabled={disabled}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onJump(stage.key);
              }}
              className={cn(
                "flex items-center gap-1.5 whitespace-nowrap px-1.5 py-0.5 rounded transition-colors",
                isActive && "text-bodhi-teal font-medium",
                isCompleted &&
                  "text-bodhi-teal/80 hover:text-bodhi-teal hover:bg-bodhi-teal/5",
                !isActive && !isCompleted && !isLocked && "text-gray-500",
                isLocked && "text-gray-400",
                disabled ? "cursor-not-allowed" : "cursor-pointer",
              )}
            >
              <span
                className={cn(
                  "w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0",
                  isActive && "bg-bodhi-teal text-white",
                  isCompleted && "bg-bodhi-teal/80 text-white",
                  !isActive && !isCompleted && !isLocked &&
                    "border border-gray-300",
                  isLocked && "border border-gray-300 bg-gray-50",
                )}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-2.5 h-2.5" aria-hidden />
                ) : isLocked ? (
                  <Lock className="w-2 h-2 text-gray-400" aria-hidden />
                ) : null}
              </span>
              {stage.label}
            </button>
          );

          const wrapped = isLocked ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>{button}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {lockReasonFor(stage.key, completion, stages)}
              </TooltipContent>
            </Tooltip>
          ) : (
            button
          );

          return (
            <div key={stage.key} className="contents">
              {wrapped}
              {idx < stages.length - 1 && (
                <span className="text-gray-300" aria-hidden>
                  →
                </span>
              )}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

interface StageProgressBarProps {
  incident: Incident;
  activeStage: ChatTabStage;
  className?: string;
}

// Thin progress bar that mirrors the tracker. Counts completed stages
// (active stage doesn't count yet) plus a small partial credit for the
// active stage so the bar always nudges forward as you move.
export const StageProgressBar: React.FC<StageProgressBarProps> = ({
  incident,
  activeStage,
  className,
}) => {
  const stages = [
    ...STAGE_META,
    ...(showsExtendedStage(incident) ? [EXTENDED_STAGE] : []),
  ];
  const completion = getStageCompletion(incident);
  const total = stages.length;
  const completedCount = stages.filter((s) => completion[s.key]).length;
  const activeIdx = stages.findIndex((s) => s.key === activeStage);
  // Half-credit for being on the active step but not yet done.
  const partial =
    activeIdx >= 0 && !completion[activeStage] ? 0.5 : 0;
  const pct = Math.min(100, Math.round(((completedCount + partial) / total) * 100));

  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
        <span>
          Step {Math.max(1, activeIdx + 1)} of {total} · {stages[activeIdx]?.label ?? ""}
        </span>
        <span>{pct}%</span>
      </div>
      <div
        className="h-1 w-full bg-gray-200 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-bodhi-teal transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
